import React, { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';

const principles = [
  {
    number: '01',
    title: 'Work Alignment',
    subtitle: 'सत्कर्म — Right Action',
    body: 'We align technology with natural rhythms and mindful living, rejecting pressure-driven models that burn out the very minds building the future. Our projects breathe. Our teams thrive.',
    accent: '#E8732A',
  },
  {
    number: '02',
    title: 'Purpose-Driven Impact',
    subtitle: 'धर्म — Sacred Duty',
    body: 'Social good, not just commercial gain. Every line of code we write carries intent beyond the bottom line. Revenue flows back into charitable missions, creating a cycle of meaningful impact.',
    accent: '#C9A84C',
  },
  {
    number: '03',
    title: 'Professional Wellness',
    subtitle: 'स्वास्थ्य — Holistic Health',
    body: 'We create serene, nature-integrated workspaces that honor the human behind the keyboard. Clarity, purpose, and inner stability are not perks — they are prerequisites for great work.',
    accent: '#E8732A',
  },
  {
    number: '04',
    title: 'Clarity Before Code',
    subtitle: 'विवेक — Discerning Wisdom',
    body: 'Our defining principle. Before writing a single line, we seek absolute clarity on the problem, the solution, and the human context. Well-understood problems yield elegant, enduring solutions.',
    accent: '#C9A84C',
  },
];

export default function Principles() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  return (
    <section id="principles" ref={ref} style={{
      padding: '120px 5%',
      background: 'linear-gradient(180deg, #0D0D0D 0%, #0f0d09 100%)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Animated connecting line */}
      <div style={{
        position: 'absolute',
        left: '50%',
        top: '15%',
        bottom: '15%',
        width: 1,
        background: 'rgba(201,168,76,0.08)',
        transform: 'translateX(-50%)',
        overflow: 'hidden',
      }}>
        <motion.div
          style={{
            width: '100%',
            height: lineHeight,
            background: 'linear-gradient(to bottom, #E8732A, #C9A84C)',
            transformOrigin: 'top',
          }}
        />
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          style={{ textAlign: 'center', marginBottom: 100 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 20 }}>
            <div style={{ width: 60, height: 1, background: 'linear-gradient(to right, transparent, #C9A84C)' }} />
            <span style={{ fontFamily: 'Cinzel', fontSize: 11, letterSpacing: 4, color: '#C9A84C', textTransform: 'uppercase' }}>How We Work</span>
            <div style={{ width: 60, height: 1, background: 'linear-gradient(to left, transparent, #C9A84C)' }} />
          </div>
          <h2 style={{
            fontFamily: 'Cinzel',
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            color: '#F5F0E8',
            letterSpacing: 2,
          }}>
            Core Principles
          </h2>
        </motion.div>

        {/* Principles alternating layout */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 80 }}>
          {principles.map((p, i) => (
            <motion.div
              key={p.number}
              initial={{ opacity: 0, x: i % 2 === 0 ? -60 : 60 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: i * 0.15 }}
              style={{
                display: 'grid',
                gridTemplateColumns: i % 2 === 0 ? '1fr auto 1fr' : '1fr auto 1fr',
                gap: 0,
                alignItems: 'center',
              }}
            >
              {/* Left content or spacer */}
              {i % 2 === 0 ? (
                <div style={{ paddingRight: 60 }}>
                  <PrincipleContent p={p} />
                </div>
              ) : (
                <div />
              )}

              {/* Center node */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
                <div style={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  border: `2px solid ${p.accent}`,
                  background: `rgba(${p.accent === '#E8732A' ? '232,115,42' : '201,168,76'},0.08)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Cinzel',
                  fontSize: 13,
                  color: p.accent,
                  letterSpacing: 1,
                  flexShrink: 0,
                  boxShadow: `0 0 20px ${p.accent}30`,
                }}>
                  {p.number}
                </div>
              </div>

              {/* Right content or spacer */}
              {i % 2 !== 0 ? (
                <div style={{ paddingLeft: 60 }}>
                  <PrincipleContent p={p} />
                </div>
              ) : (
                <div />
              )}
            </motion.div>
          ))}
        </div>

        {/* Responsive: stack on mobile */}
        <style>{`
          @media (max-width: 640px) {
            .principle-grid { grid-template-columns: auto 1fr !important; }
          }
        `}</style>
      </div>
    </section>
  );
}

function PrincipleContent({ p }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.02)',
      border: `1px solid rgba(${p.accent === '#E8732A' ? '232,115,42' : '201,168,76'},0.12)`,
      borderRadius: 12,
      padding: '32px',
    }}>
      <div style={{ fontSize: 11, letterSpacing: 3, color: p.accent, textTransform: 'uppercase', marginBottom: 12, fontFamily: 'Cinzel' }}>
        {p.subtitle}
      </div>
      <h3 style={{ fontFamily: 'Cinzel', fontSize: 22, color: '#F5F0E8', marginBottom: 16, letterSpacing: 1 }}>
        {p.title}
      </h3>
      <p style={{ color: '#B0A898', lineHeight: 1.85, fontSize: 15 }}>
        {p.body}
      </p>
    </div>
  );
}
